import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { fetcher } from './fetcher';

interface MutationOperation {
  query: string;
  variables?: unknown;
}

export function useBatchMutation<TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<unknown[], TError, MutationOperation[], TContext>,
    'mutationFn'
  >,
): UseMutationResult<unknown[], TError, MutationOperation[], TContext> {
  return useMutation<unknown[], TError, MutationOperation[], TContext>({
    mutationFn: async (operations) => {
      const variableDefinitions: string[] = [];
      const mutationFields: string[] = [];
      const fragmentsSet = new Set<string>();
      const variables: Record<string, unknown> = {};

      operations.forEach((op, idx) => {
        const parts = op.query.split(/(?=fragment\s+)/);
        const mutationPart = parts[0];
        const fragmentParts = parts.slice(1);

        const mutationMatch = mutationPart.match(
          /mutation\s+\w+(\([^)]+\))?\s*{([\s\S]*)}\s*$/,
        );
        if (!mutationMatch) {
          throw new Error(`Invalid mutation format at index ${idx}`);
        }

        const [, params, body] = mutationMatch;

        if (!params) {
          fragmentParts.forEach((fragment) => {
            const trimmed = fragment.trim();
            if (trimmed) {
              fragmentsSet.add(trimmed);
            }
          });
          return;
        }

        const paramMatch = params.match(/\$\w+:\s*([^)!]+!?)/);
        if (!paramMatch) {
          fragmentParts.forEach((fragment) => {
            const trimmed = fragment.trim();
            if (trimmed) {
              fragmentsSet.add(trimmed);
            }
          });
          return;
        }

        const typeName = paramMatch[1];
        const varName = `var${idx}`;
        const aliasName = `m${idx}`;
        variableDefinitions.push(`$${varName}: ${typeName}`);

        const bodyWithReplacedVar = body
          .replace(/\$\w+/g, `$${varName}`)
          .trim();

        // Add alias to the top-level mutation field.
        // Heuristic: prefix the first field name before any argument list or selection set with `${aliasName}: `
        const aliasedBody = bodyWithReplacedVar.replace(
          /^(\s*)([A-Za-z_][A-Za-z0-9_]*)\s*([({])/m,
          (_match, leading, fieldName, nextChar) => {
            const lead = String(leading);
            const name = String(fieldName);
            const nxt = String(nextChar);
            return `${lead}${aliasName}: ${name}${nxt}`;
          },
        );

        mutationFields.push(aliasedBody);

        if (op.variables && typeof op.variables === 'object') {
          const varsObj = op.variables as Record<string, unknown>;
          const firstKey = Object.keys(varsObj)[0];
          if (firstKey) {
            variables[varName] = varsObj[firstKey];
          }
        }

        fragmentParts.forEach((fragment) => {
          const trimmed = fragment.trim();
          if (trimmed) {
            fragmentsSet.add(trimmed);
          }
        });
      });

      const combinedQuery = `
        mutation BatchMutation(${variableDefinitions.join(', ')}) {
          ${mutationFields.join('\n  ')}
        }
        ${Array.from(fragmentsSet).join('\n')}
      `;

      const data = await fetcher<
        Record<string, unknown>,
        Record<string, unknown>
      >(combinedQuery, variables)();

      return Object.values(data);
    },
    ...options,
  });
}
